import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FiShoppingBag,
  FiFilter,
  FiSearch,
  FiRefreshCw,
  FiDownload,
  FiDollarSign,
  FiTrendingUp,
  FiTrendingDown,
  FiBox,
  FiFileText,
  FiEye,
  FiX,
  FiCalendar,
  FiUser,
  FiAlertCircle,
  FiArrowUpRight,
  FiArrowDownRight,
  FiTarget,
  FiLayers,
  FiCheckSquare,
  FiChevronDown,
  FiHelpCircle
} from 'react-icons/fi';
import { vendasService } from '../../services/api';
import { BIPeriodFilter, BIFilterSelect, BIPagination } from '../common';
import Loading from '../common/Loading';
import './Vendas.css';

function formatCurrency(val) {
  const num = Number(val || 0);
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatNumber(val, decimals = 2) {
  const num = Number(val || 0);
  return num.toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const cleanDate = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const parts = cleanDate.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
}

function getCurrentMonthPeriod() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const lastDay = String(new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()).padStart(2, '0');
  return {
    date_start: `${y}-${m}-01`,
    date_end: `${y}-${m}-${lastDay}`
  };
}

export default function Vendas() {
  // Estados de dados
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({
    total_records: 0,
    total_orders: 0,
    total_quantity: 0,
    total_gross: 0,
    total_returns: 0,
    total_net: 0,
    sales_count: 0,
    returns_count: 0
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 25,
    total: 0,
    last_page: 1
  });

  // Filtros de seleção
  const [filtersData, setFiltersData] = useState({
    filiais: [],
    metas: [],
    colaboradores: [],
    tipos: []
  });

  // Filtros aplicados
  const [filters, setFilters] = useState(() => {
    const curMonth = getCurrentMonthPeriod();
    return {
      search: '',
      goal_id: 'all',
      branch: 'all',
      vendor: 'all',
      sale_type: 'all',
      date_start: curMonth.date_start,
      date_end: curMonth.date_end
    };
  });

  // Input de busca textual
  const [searchInput, setSearchInput] = useState('');

  // ─── Estados dos Modais Suspensos (Popovers) ─────────────────────────
  // 1. Filiais
  const [openBranchDropdown, setOpenBranchDropdown] = useState(false);
  const [branchSearch, setBranchSearch] = useState('');
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [tempSelectedBranches, setTempSelectedBranches] = useState([]);
  const branchDropdownRef = useRef(null);

  // 2. Vendedores / Colaboradores
  const [openVendorDropdown, setOpenVendorDropdown] = useState(false);
  const [vendorSearch, setVendorSearch] = useState('');
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [tempSelectedVendors, setTempSelectedVendors] = useState([]);
  const vendorDropdownRef = useRef(null);

  // 3. Metas Vinculadas
  const [openGoalDropdown, setOpenGoalDropdown] = useState(false);
  const [goalSearch, setGoalSearch] = useState('');
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [tempSelectedGoals, setTempSelectedGoals] = useState([]);
  const goalDropdownRef = useRef(null);

  // 4. Buscar Pedido / Cliente
  const [openSearchDropdown, setOpenSearchDropdown] = useState(false);
  const [selectedSearchItems, setSelectedSearchItems] = useState([]);
  const [tempSelectedSearchItems, setTempSelectedSearchItems] = useState([]);
  const [liveSuggestions, setLiveSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const searchDropdownRef = useRef(null);

  // Ordenação
  const [sortField, setSortField] = useState('sale_date');
  const [sortDirection, setSortDirection] = useState('DESC');

  // Estados de UI
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSale, setSelectedSale] = useState(null);

  // Inicializar sugestões de pedidos quando filtersData for carregado
  useEffect(() => {
    if (filtersData.recent_orders && filtersData.recent_orders.length > 0) {
      setLiveSuggestions(filtersData.recent_orders);
    }
  }, [filtersData.recent_orders]);

  // Fechar popovers ao clicar fora
  useEffect(() => {
    function handleClickOutside(event) {
      if (branchDropdownRef.current && !branchDropdownRef.current.contains(event.target)) {
        setOpenBranchDropdown(false);
      }
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target)) {
        setOpenVendorDropdown(false);
      }
      if (goalDropdownRef.current && !goalDropdownRef.current.contains(event.target)) {
        setOpenGoalDropdown(false);
      }
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
        setOpenSearchDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Busca em tempo real de sugestões de Pedido / Cliente com debounce
  useEffect(() => {
    if (!openSearchDropdown) return;
    const query = searchInput.trim();
    if (query.length < 2) {
      setLiveSuggestions(filtersData.recent_orders || []);
      return;
    }
    const timer = setTimeout(async () => {
      setLoadingSuggestions(true);
      try {
        const res = await vendasService.getSearchSuggestions({
          q: query,
          branch: filters.branch !== 'all' ? filters.branch : undefined
        });
        if (res?.data?.suggestions) {
          setLiveSuggestions(res.data.suggestions);
        }
      } catch (err) {
        console.error('Erro ao buscar sugestões de pedidos:', err);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [searchInput, openSearchDropdown, filtersData.recent_orders, filters.branch]);

  // ─── 1. Lógica do Dropdown de Filiais ───────────────────────────────
  const filteredBranches = useMemo(() => {
    const list = filtersData.filiais || [];
    if (!branchSearch.trim()) return list;
    const q = branchSearch.toLowerCase().trim();
    return list.filter(f =>
      String(f.codfilial || '').toLowerCase().includes(q) ||
      String(f.nomefantasia || '').toLowerCase().includes(q) ||
      String(f.razaosocial || '').toLowerCase().includes(q)
    );
  }, [filtersData.filiais, branchSearch]);

  const allFilteredBranchesSelected = useMemo(() => {
    if (filteredBranches.length === 0) return false;
    return filteredBranches.every(f => tempSelectedBranches.includes(String(f.codfilial)));
  }, [filteredBranches, tempSelectedBranches]);

  const someFilteredBranchesSelected = useMemo(() => {
    return filteredBranches.some(f => tempSelectedBranches.includes(String(f.codfilial)));
  }, [filteredBranches, tempSelectedBranches]);

  const handleToggleBranch = (cod) => {
    const sCod = String(cod);
    setTempSelectedBranches(prev =>
      prev.includes(sCod) ? prev.filter(x => x !== sCod) : [...prev, sCod]
    );
  };

  const handleToggleSelectAllBranches = () => {
    if (allFilteredBranchesSelected) {
      const filteredCodes = new Set(filteredBranches.map(f => String(f.codfilial)));
      setTempSelectedBranches(prev => prev.filter(c => !filteredCodes.has(c)));
    } else {
      const newSet = new Set(tempSelectedBranches);
      filteredBranches.forEach(f => newSet.add(String(f.codfilial)));
      setTempSelectedBranches(Array.from(newSet));
    }
  };

  const handleClearBranchSelection = (e) => {
    if (e) e.stopPropagation();
    setTempSelectedBranches([]);
    setSelectedBranches([]);
    setBranchSearch('');
    const updatedFilters = { ...filters, branch: 'all' };
    setFilters(updatedFilters);
    loadVendas(1, updatedFilters);
  };

  const handleApplyBranchSelection = () => {
    setSelectedBranches(tempSelectedBranches);
    setOpenBranchDropdown(false);
    const branchVal = tempSelectedBranches.length === 0 || tempSelectedBranches.length === (filtersData.filiais || []).length
      ? 'all'
      : tempSelectedBranches.join(',');
    const updatedFilters = { ...filters, branch: branchVal };
    setFilters(updatedFilters);
    loadVendas(1, updatedFilters);
  };

  // ─── 2. Lógica do Dropdown de Colaboradores ─────────────────────────
  const filteredVendors = useMemo(() => {
    const list = filtersData.colaboradores || [];
    if (!vendorSearch.trim()) return list;
    const q = vendorSearch.toLowerCase().trim();
    return list.filter(c =>
      String(c.nome || '').toLowerCase().includes(q) ||
      String(c.code || '').toLowerCase().includes(q) ||
      String(c.codfilial || '').toLowerCase().includes(q) ||
      String(c.type || '').toLowerCase().includes(q)
    );
  }, [filtersData.colaboradores, vendorSearch]);

  const allFilteredVendorsSelected = useMemo(() => {
    if (filteredVendors.length === 0) return false;
    return filteredVendors.every(c => tempSelectedVendors.includes(String(c.code)));
  }, [filteredVendors, tempSelectedVendors]);

  const someFilteredVendorsSelected = useMemo(() => {
    return filteredVendors.some(c => tempSelectedVendors.includes(String(c.code)));
  }, [filteredVendors, tempSelectedVendors]);

  const handleToggleVendor = (code) => {
    const sCode = String(code);
    setTempSelectedVendors(prev =>
      prev.includes(sCode) ? prev.filter(x => x !== sCode) : [...prev, sCode]
    );
  };

  const handleToggleSelectAllVendors = () => {
    if (allFilteredVendorsSelected) {
      const filteredCodes = new Set(filteredVendors.map(c => String(c.code)));
      setTempSelectedVendors(prev => prev.filter(c => !filteredCodes.has(c)));
    } else {
      const newSet = new Set(tempSelectedVendors);
      filteredVendors.forEach(c => newSet.add(String(c.code)));
      setTempSelectedVendors(Array.from(newSet));
    }
  };

  const handleClearVendorSelection = (e) => {
    if (e) e.stopPropagation();
    setTempSelectedVendors([]);
    setSelectedVendors([]);
    setVendorSearch('');
    const updatedFilters = { ...filters, vendor: 'all' };
    setFilters(updatedFilters);
    loadVendas(1, updatedFilters);
  };

  const handleApplyVendorSelection = () => {
    setSelectedVendors(tempSelectedVendors);
    setOpenVendorDropdown(false);
    const vendorVal = tempSelectedVendors.length === 0 || tempSelectedVendors.length === (filtersData.colaboradores || []).length
      ? 'all'
      : tempSelectedVendors.join(',');
    const updatedFilters = { ...filters, vendor: vendorVal };
    setFilters(updatedFilters);
    loadVendas(1, updatedFilters);
  };

  // ─── 3. Lógica do Dropdown de Metas ─────────────────────────────────
  const filteredGoals = useMemo(() => {
    const list = filtersData.metas || [];
    if (!goalSearch.trim()) return list;
    const q = goalSearch.toLowerCase().trim();
    return list.filter(m =>
      String(m.id || '').toLowerCase().includes(q) ||
      String(m.label || '').toLowerCase().includes(q) ||
      String(m.codfilial || '').toLowerCase().includes(q)
    );
  }, [filtersData.metas, goalSearch]);

  const allFilteredGoalsSelected = useMemo(() => {
    if (filteredGoals.length === 0) return false;
    return filteredGoals.every(m => tempSelectedGoals.includes(m.id));
  }, [filteredGoals, tempSelectedGoals]);

  const someFilteredGoalsSelected = useMemo(() => {
    return filteredGoals.some(m => tempSelectedGoals.includes(m.id));
  }, [filteredGoals, tempSelectedGoals]);

  const handleToggleGoal = (id) => {
    const nId = Number(id);
    setTempSelectedGoals(prev =>
      prev.includes(nId) ? prev.filter(x => x !== nId) : [...prev, nId]
    );
  };

  const handleToggleSelectAllGoals = () => {
    if (allFilteredGoalsSelected) {
      const filteredIds = new Set(filteredGoals.map(m => m.id));
      setTempSelectedGoals(prev => prev.filter(id => !filteredIds.has(id)));
    } else {
      const newSet = new Set(tempSelectedGoals);
      filteredGoals.forEach(m => newSet.add(m.id));
      setTempSelectedGoals(Array.from(newSet));
    }
  };

  const handleClearGoalSelection = (e) => {
    if (e) e.stopPropagation();
    setTempSelectedGoals([]);
    setSelectedGoals([]);
    setGoalSearch('');
    const updatedFilters = { ...filters, goal_id: 'all' };
    setFilters(updatedFilters);
    loadVendas(1, updatedFilters);
  };

  const handleApplyGoalSelection = () => {
    setSelectedGoals(tempSelectedGoals);
    setOpenGoalDropdown(false);
    const goalVal = tempSelectedGoals.length === 0 || tempSelectedGoals.length === (filtersData.metas || []).length
      ? 'all'
      : tempSelectedGoals.join(',');
    const updatedFilters = { ...filters, goal_id: goalVal };
    setFilters(updatedFilters);
    loadVendas(1, updatedFilters);
  };

  // ─── 4. Lógica do Dropdown de Buscar Pedido / Cliente ───────────────
  const allFilteredSearchSelected = useMemo(() => {
    if (liveSuggestions.length === 0) return false;
    return liveSuggestions.every(item => tempSelectedSearchItems.includes(String(item.order_number)));
  }, [liveSuggestions, tempSelectedSearchItems]);

  const someFilteredSearchSelected = useMemo(() => {
    return liveSuggestions.some(item => tempSelectedSearchItems.includes(String(item.order_number)));
  }, [liveSuggestions, tempSelectedSearchItems]);

  const handleToggleSearchOrder = (orderNumber) => {
    const sOrder = String(orderNumber);
    setTempSelectedSearchItems(prev =>
      prev.includes(sOrder) ? prev.filter(x => x !== sOrder) : [...prev, sOrder]
    );
  };

  const handleToggleSelectAllSearch = () => {
    if (allFilteredSearchSelected) {
      const filteredOrders = new Set(liveSuggestions.map(item => String(item.order_number)));
      setTempSelectedSearchItems(prev => prev.filter(x => !filteredOrders.has(x)));
    } else {
      const newSet = new Set(tempSelectedSearchItems);
      liveSuggestions.forEach(item => newSet.add(String(item.order_number)));
      setTempSelectedSearchItems(Array.from(newSet));
    }
  };

  const handleClearSearchSelection = (e) => {
    if (e) e.stopPropagation();
    setSearchInput('');
    setSelectedSearchItems([]);
    setTempSelectedSearchItems([]);
    const updatedFilters = { ...filters, search: '' };
    setFilters(updatedFilters);
    loadVendas(1, updatedFilters);
  };

  const handleApplySearchSelection = () => {
    setSelectedSearchItems(tempSelectedSearchItems);
    setOpenSearchDropdown(false);
    let searchVal = '';
    if (tempSelectedSearchItems.length > 0) {
      searchVal = tempSelectedSearchItems.join(',');
    } else {
      searchVal = searchInput.trim();
    }
    const updatedFilters = { ...filters, search: searchVal };
    setFilters(updatedFilters);
    loadVendas(1, updatedFilters);
  };

  // Carregar dados para os filtros (dropdowns)
  const loadFiltersData = useCallback(async () => {
    try {
      const res = await vendasService.getFiltersData();
      if (res?.data) {
        setFiltersData(res.data);
      }
    } catch (err) {
      console.error('Erro ao carregar opções de filtros:', err);
    }
  }, []);

  // Carregar lista de vendas
  const loadVendas = useCallback(async (pageToLoad = 1, currentFilters = filters, currentSort = { field: sortField, dir: sortDirection }) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pageToLoad,
        per_page: pagination.per_page,
        sort_field: currentSort.field,
        sort_direction: currentSort.dir
      };

      if (currentFilters.search) params.search = currentFilters.search;
      if (currentFilters.goal_id && currentFilters.goal_id !== 'all') params.goal_id = currentFilters.goal_id;
      if (currentFilters.branch && currentFilters.branch !== 'all') params.branch = currentFilters.branch;
      if (currentFilters.vendor && currentFilters.vendor !== 'all') params.vendor = currentFilters.vendor;
      if (currentFilters.sale_type && currentFilters.sale_type !== 'all') params.sale_type = currentFilters.sale_type;
      if (currentFilters.date_start) params.date_start = currentFilters.date_start;
      if (currentFilters.date_end) params.date_end = currentFilters.date_end;

      const res = await vendasService.getAll(params);
      if (res?.data) {
        setItems(res.data.items || []);
        if (res.data.summary) {
          setSummary(res.data.summary);
        }
        if (res.data.pagination) {
          setPagination(res.data.pagination);
        }
      }
    } catch (err) {
      console.error('Erro ao carregar vendas:', err);
      setError(err?.response?.data?.error || 'Erro ao carregar lista de vendas.');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.per_page, sortField, sortDirection]);

  // Carga inicial
  useEffect(() => {
    loadFiltersData();
    loadVendas(1);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Submeter filtros
  const handleApplyFilters = (e) => {
    if (e) e.preventDefault();
    let searchVal = '';
    if (selectedSearchItems.length > 0) {
      searchVal = selectedSearchItems.join(',');
    } else {
      searchVal = searchInput.trim();
    }
    const updatedFilters = {
      ...filters,
      search: searchVal
    };
    setFilters(updatedFilters);
    loadVendas(1, updatedFilters);
  };

  // Limpar filtros
  const handleResetFilters = () => {
    const curMonth = getCurrentMonthPeriod();
    const reset = {
      search: '',
      goal_id: 'all',
      branch: 'all',
      vendor: 'all',
      sale_type: 'all',
      date_start: curMonth.date_start,
      date_end: curMonth.date_end
    };
    setSearchInput('');
    setSelectedSearchItems([]);
    setTempSelectedSearchItems([]);
    setSelectedBranches([]);
    setTempSelectedBranches([]);
    setBranchSearch('');
    setSelectedVendors([]);
    setTempSelectedVendors([]);
    setVendorSearch('');
    setSelectedGoals([]);
    setTempSelectedGoals([]);
    setGoalSearch('');
    setFilters(reset);
    loadVendas(1, reset);
  };

  // Ordenação ao clicar no cabeçalho da tabela
  const handleSort = (field) => {
    let newDir = 'DESC';
    if (sortField === field) {
      newDir = sortDirection === 'DESC' ? 'ASC' : 'DESC';
    }
    setSortField(field);
    setSortDirection(newDir);
    loadVendas(pagination.current_page, filters, { field, dir: newDir });
  };

  // Mudança de página
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.last_page) return;
    loadVendas(newPage);
  };

  // Mudança de itens por página
  const handlePerPageChange = (newPerPage) => {
    setPagination(prev => ({ ...prev, per_page: newPerPage }));
    setLoading(true);
    const params = {
      page: 1,
      per_page: newPerPage,
      sort_field: sortField,
      sort_direction: sortDirection
    };
    if (filters.search) params.search = filters.search;
    if (filters.goal_id && filters.goal_id !== 'all') params.goal_id = filters.goal_id;
    if (filters.branch && filters.branch !== 'all') params.branch = filters.branch;
    if (filters.vendor && filters.vendor !== 'all') params.vendor = filters.vendor;
    if (filters.sale_type && filters.sale_type !== 'all') params.sale_type = filters.sale_type;
    if (filters.date_start) params.date_start = filters.date_start;
    if (filters.date_end) params.date_end = filters.date_end;

    vendasService.getAll(params)
      .then(res => {
        if (res?.data) {
          setItems(res.data.items || []);
          if (res.data.summary) setSummary(res.data.summary);
          if (res.data.pagination) setPagination(res.data.pagination);
        }
      })
      .catch(err => setError('Erro ao alterar quantidade por página.'))
      .finally(() => setLoading(false));
  };

  // Exportar Excel / CSV
  const handleExport = async () => {
    setExporting(true);
    try {
      const payload = {
        search: filters.search,
        goal_id: filters.goal_id !== 'all' ? filters.goal_id : undefined,
        branch: filters.branch !== 'all' ? filters.branch : undefined,
        vendor: filters.vendor !== 'all' ? filters.vendor : undefined,
        sale_type: filters.sale_type !== 'all' ? filters.sale_type : undefined,
        date_start: filters.date_start || undefined,
        date_end: filters.date_end || undefined
      };

      const response = await vendasService.exportExcel(payload);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vendas_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro na exportação:', err);
      alert('Falha ao exportar relatório de vendas.');
    } finally {
      setExporting(false);
    }
  };

  // Contagem de filtros ativos
  const activeFiltersCount = useMemo(() => {
    let c = 0;
    if (filters.search) c++;
    if (filters.goal_id && filters.goal_id !== 'all') c++;
    if (filters.branch && filters.branch !== 'all') c++;
    if (filters.vendor && filters.vendor !== 'all') c++;
    if (filters.sale_type && filters.sale_type !== 'all') c++;
    if (filters.date_start || filters.date_end) c++;
    return c;
  }, [filters]);

  const isDevolucaoSelected = selectedSale?.sale_type === 'devolucao';

  return (
    <div className="container-fluid py-4">
      {/* Cabeçalho da Seção */}
      <div className="mb-4 d-flex justify-content-between align-items-start flex-wrap gap-3">
        <div>
          <h2 className="mb-1">Gestão de Vendas</h2>
          <p className="text-muted mb-0">
            Consulta consolidada de vendas e devoluções com filtros por meta, filial, vendedor e tipo de transação.
          </p>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn-modern btn-modern-light"
            onClick={() => loadVendas(pagination.current_page)}
            disabled={loading}
            title="Atualizar dados"
          >
            <FiRefreshCw size={16} className={loading ? 'spin-ico' : ''} />
          </button>

          <button
            type="button"
            className="btn-modern btn-modern-primary"
            onClick={handleExport}
            disabled={exporting || summary.total_records === 0}
            title="Exportar dados para CSV"
          >
            <FiDownload size={15} />
            <span>{exporting ? 'Exportando...' : 'Exportar CSV'}</span>
          </button>
        </div>
      </div>

      {/* Alerta de Erro se houver */}
      {error && (
        <div className="alert alert-danger d-flex align-items-center gap-2 mb-4">
          <FiAlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Cards de Métricas / KPIs */}
      <div className="stats-grid-modern mb-4">
        {/* Card 1: Total Líquido */}
        <div className="stat-card-modern">
          <div className="stat-icon-container bg-success-light text-success">
            <FiDollarSign size={24} />
          </div>
          <div className="stat-content flex-grow-1 min-w-0">
            <div className="stat-label-modern">Faturamento Líquido</div>
            <div
              className="stat-value-modern"
              style={{ color: summary.total_net >= 0 ? 'var(--text-main)' : 'var(--danger)' }}
            >
              {formatCurrency(summary.total_net)}
            </div>
            <small className="text-muted d-block text-truncate">Receita final consolidada</small>
          </div>
        </div>

        {/* Card 2: Total Bruto */}
        <div className="stat-card-modern">
          <div className="stat-icon-container bg-primary-light text-primary">
            <FiTrendingUp size={24} />
          </div>
          <div className="stat-content flex-grow-1 min-w-0">
            <div className="stat-label-modern">Faturamento Bruto</div>
            <div className="stat-value-modern">{formatCurrency(summary.total_gross)}</div>
            <small className="text-muted d-block text-truncate">Total antes de devoluções</small>
          </div>
        </div>

        {/* Card 3: Devoluções */}
        <div className="stat-card-modern">
          <div className="stat-icon-container bg-danger-light text-danger">
            <FiTrendingDown size={24} />
          </div>
          <div className="stat-content flex-grow-1 min-w-0">
            <div className="stat-label-modern">Devoluções</div>
            <div className="stat-value-modern" style={{ color: 'var(--danger)' }}>
              {formatCurrency(summary.total_returns)}
            </div>
            <small className="text-muted d-block text-truncate">
              {summary.returns_count} {summary.returns_count === 1 ? 'devolução' : 'devoluções'}
            </small>
          </div>
        </div>

        {/* Card 4: Quantidade Total de Peças */}
        <div className="stat-card-modern">
          <div className="stat-icon-container bg-warning-light" style={{ color: '#d97706' }}>
            <FiBox size={24} />
          </div>
          <div className="stat-content flex-grow-1 min-w-0">
            <div className="stat-label-modern">Volume de Itens</div>
            <div className="stat-value-modern">
              {formatNumber(summary.total_quantity, 0)}
              <span style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-muted)' }}>un</span>
            </div>
            <small className="text-muted d-block text-truncate">Volume físico movimentado</small>
          </div>
        </div>

        {/* Card 5: Total de Pedidos */}
        <div className="stat-card-modern">
          <div className="stat-icon-container bg-info-light text-info">
            <FiFileText size={24} />
          </div>
          <div className="stat-content flex-grow-1 min-w-0">
            <div className="stat-label-modern">Total de Pedidos</div>
            <div className="stat-value-modern">{summary.total_orders}</div>
            <small className="text-muted d-block text-truncate">
              {summary.sales_count} vendas | {summary.returns_count} dev.
            </small>
          </div>
        </div>
      </div>

      {/* Painel de Filtros Padronizado no Estilo BI */}
      <div className="bi-toolbar mb-4" style={{ overflow: 'visible', position: 'relative', zIndex: 30 }}>
        <form onSubmit={handleApplyFilters}>
          <div className="row g-3 align-items-end">
            {/* 1. Busca Pedido / Cliente - Modal Suspenso */}
            <div className="col-12 col-sm-6 col-xl-4">
              <div className="form-group-modern mb-0">
                <span className="bi-filter-label">Buscar Pedido / Cliente</span>
                  <div
                    className={`vendas-search-dropdown-container ${openSearchDropdown ? 'is-open' : ''}`}
                    ref={searchDropdownRef}
                    style={{ position: 'relative', zIndex: openSearchDropdown ? 1060 : 'auto' }}
                  >
                    <div className="vendas-search-input-wrapper">
                      <FiSearch
                        size={15}
                        style={{
                          position: 'absolute',
                          left: '11px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: 'var(--text-muted)',
                          pointerEvents: 'none',
                          zIndex: 2
                        }}
                      />
                      <input
                        type="text"
                        className="form-control-modern vendas-search-input-box"
                        placeholder={
                          selectedSearchItems.length > 0
                            ? `${selectedSearchItems.length} pedido(s) selecionado(s)`
                            : "Nº pedido, cliente ou código..."
                        }
                        value={
                          selectedSearchItems.length > 0 && !openSearchDropdown
                            ? `${selectedSearchItems.length} pedido(s) selecionado(s)`
                            : searchInput
                        }
                        onChange={(e) => {
                          setSearchInput(e.target.value);
                          if (!openSearchDropdown) setOpenSearchDropdown(true);
                        }}
                        onFocus={() => {
                          setTempSelectedSearchItems(selectedSearchItems);
                          setOpenSearchDropdown(true);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleApplySearchSelection();
                          } else if (e.key === 'Escape') {
                            setOpenSearchDropdown(false);
                          }
                        }}
                        style={{ paddingLeft: '34px' }}
                      />

                      <div className="vendas-search-actions-inside">
                        {selectedSearchItems.length > 0 && (
                          <span className="vendas-selected-badge" title="Pedidos selecionados">
                            <FiCheckSquare size={12} /> {selectedSearchItems.length}
                          </span>
                        )}
                        {(searchInput || selectedSearchItems.length > 0) && (
                          <FiX
                            size={14}
                            className="vendas-search-clear-icon"
                            title="Limpar busca e seleção"
                            onClick={handleClearSearchSelection}
                          />
                        )}
                        <FiChevronDown
                          size={15}
                          className={`vendas-search-chevron ${openSearchDropdown ? 'open' : ''}`}
                          onClick={() => {
                            if (!openSearchDropdown) {
                              setTempSelectedSearchItems(selectedSearchItems);
                            }
                            setOpenSearchDropdown(!openSearchDropdown);
                          }}
                        />
                      </div>
                    </div>

                    {/* Popover Suspenso - Pedido / Cliente */}
                    {openSearchDropdown && (
                      <div className="vendas-search-popover popover-wide">
                        {/* Header do Popover */}
                        <div className="vendas-popover-header">
                          <label className="vendas-popover-select-all">
                            <input
                              type="checkbox"
                              checked={allFilteredSearchSelected}
                              ref={(el) => {
                                if (el) {
                                  el.indeterminate = someFilteredSearchSelected && !allFilteredSearchSelected;
                                }
                              }}
                              onChange={handleToggleSelectAllSearch}
                            />
                            <span>Selecionar todos ({liveSuggestions.length})</span>
                          </label>

                          <div className="vendas-popover-header-actions">
                            {tempSelectedSearchItems.length > 0 && (
                              <button
                                type="button"
                                className="vendas-popover-clear-btn"
                                onClick={() => setTempSelectedSearchItems([])}
                              >
                                Desmarcar Todos
                              </button>
                            )}
                            <span className="vendas-popover-count-tag">
                              {tempSelectedSearchItems.length} / {liveSuggestions.length}
                            </span>
                          </div>
                        </div>

                        {/* Lista de Sugestões de Pedidos / Clientes */}
                        <div className="vendas-popover-list">
                          {loadingSuggestions ? (
                            <div className="vendas-popover-empty">
                              <FiRefreshCw size={18} className="spin-ico d-block mx-auto mb-1 text-primary" />
                              Buscando sugestões...
                            </div>
                          ) : liveSuggestions.length === 0 ? (
                            <div className="vendas-popover-empty">
                              <FiHelpCircle size={24} className="d-block mx-auto mb-1 opacity-50" />
                              Nenhum pedido ou cliente encontrado{searchInput ? ` para "${searchInput}"` : ''}.
                            </div>
                          ) : (
                            liveSuggestions.map((item, idx) => {
                              const isSelected = tempSelectedSearchItems.includes(String(item.order_number));
                              return (
                                <div
                                  key={`${item.order_number}-${idx}`}
                                  className={`vendas-popover-item ${isSelected ? 'selected' : ''}`}
                                  onClick={() => handleToggleSearchOrder(item.order_number)}
                                >
                                  <div className="vendas-popover-item-left">
                                    <input
                                      type="checkbox"
                                      className="vendas-popover-checkbox"
                                      checked={isSelected}
                                      onChange={() => {}}
                                    />
                                    <div className="vendas-popover-avatar order">
                                      <FiFileText size={13} />
                                    </div>
                                    <div className="vendas-popover-info">
                                      <span className="vendas-popover-name" title={`Pedido #${item.order_number} - ${item.customer_name}`}>
                                        Pedido #{item.order_number} – {item.customer_name}
                                      </span>
                                      <div className="vendas-popover-meta">
                                        <span>🏢 Filial {item.branch_code}</span>
                                        {item.codcli && <span>• Cód: {item.codcli}</span>}
                                        {item.sale_date && <span>• {formatDate(item.sale_date)}</span>}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="vendas-popover-item-right">
                                    <span className="vendas-popover-target-val">{formatCurrency(item.net_amount)}</span>
                                    <span className="vendas-popover-target-lbl">Líquido</span>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>

                        {/* Rodapé do Popover */}
                        <div className="vendas-popover-footer">
                          <span className="vendas-popover-footer-summary">
                            {tempSelectedSearchItems.length > 0
                              ? `${tempSelectedSearchItems.length} selecionado(s)`
                              : (searchInput ? `Texto: "${searchInput}"` : 'Todos os pedidos')}
                          </span>
                          <div className="vendas-popover-footer-btns">
                            <button
                              type="button"
                              className="btn-modern btn-modern-light btn-sm"
                              onClick={() => setOpenSearchDropdown(false)}
                            >
                              Cancelar
                            </button>
                            <button
                              type="button"
                              className="btn-modern btn-modern-primary btn-sm"
                              onClick={handleApplySearchSelection}
                            >
                              Aplicar
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 2. Filial - Modal Suspenso */}
              <div className="col-12 col-sm-6 col-xl-2">
                <div className="form-group-modern mb-0">
                  <span className="bi-filter-label">Filial</span>
                  <div
                    className={`vendas-search-dropdown-container ${openBranchDropdown ? 'is-open' : ''}`}
                    ref={branchDropdownRef}
                    style={{ position: 'relative', zIndex: openBranchDropdown ? 1060 : 'auto' }}
                  >
                    <div className="vendas-search-input-wrapper">
                      <input
                        type="text"
                        readOnly
                        className="form-control-modern vendas-search-input-box"
                        value={
                          selectedBranches.length === 0 || selectedBranches.length === (filtersData.filiais || []).length
                            ? 'Todas as Filiais'
                            : (selectedBranches.length === 1
                                ? `Filial ${selectedBranches[0]} - ${filtersData.filiais.find(f => String(f.codfilial) === String(selectedBranches[0]))?.nomefantasia || filtersData.filiais.find(f => String(f.codfilial) === String(selectedBranches[0]))?.razaosocial || ''}`
                                : `${selectedBranches.length} filiais selecionadas`)
                        }
                        onClick={() => {
                          if (!openBranchDropdown) {
                            setTempSelectedBranches(selectedBranches);
                          }
                          setOpenBranchDropdown(!openBranchDropdown);
                        }}
                      />

                      <div className="vendas-search-actions-inside">
                        {selectedBranches.length > 0 && selectedBranches.length < (filtersData.filiais || []).length && (
                          <span className="vendas-selected-badge" title="Filiais selecionadas">
                            <FiCheckSquare size={12} /> {selectedBranches.length}
                          </span>
                        )}
                        {selectedBranches.length > 0 && (
                          <FiX
                            size={14}
                            className="vendas-search-clear-icon"
                            title="Limpar seleção de filiais"
                            onClick={handleClearBranchSelection}
                          />
                        )}
                        <FiChevronDown
                          size={15}
                          className={`vendas-search-chevron ${openBranchDropdown ? 'open' : ''}`}
                          onClick={() => {
                            if (!openBranchDropdown) {
                              setTempSelectedBranches(selectedBranches);
                            }
                            setOpenBranchDropdown(!openBranchDropdown);
                          }}
                        />
                      </div>
                    </div>

                    {/* Popover Suspenso - Filial */}
                    {openBranchDropdown && (
                      <div className="vendas-search-popover">
                        {/* Campo de Busca Interno */}
                        <div className="vendas-popover-search-bar">
                          <FiSearch size={13} className="vendas-popover-search-icon" />
                          <input
                            type="text"
                            placeholder="Buscar filial por código ou nome..."
                            value={branchSearch}
                            onChange={(e) => setBranchSearch(e.target.value)}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleApplyBranchSelection();
                              } else if (e.key === 'Escape') {
                                setOpenBranchDropdown(false);
                              }
                            }}
                          />
                        </div>

                        {/* Header do Popover */}
                        <div className="vendas-popover-header">
                          <label className="vendas-popover-select-all">
                            <input
                              type="checkbox"
                              checked={allFilteredBranchesSelected}
                              ref={(el) => {
                                if (el) {
                                  el.indeterminate = someFilteredBranchesSelected && !allFilteredBranchesSelected;
                                }
                              }}
                              onChange={handleToggleSelectAllBranches}
                            />
                            <span>Selecionar todas ({filteredBranches.length})</span>
                          </label>

                          <div className="vendas-popover-header-actions">
                            {tempSelectedBranches.length > 0 && (
                              <button
                                type="button"
                                className="vendas-popover-clear-btn"
                                onClick={() => setTempSelectedBranches([])}
                              >
                                Desmarcar Todas
                              </button>
                            )}
                            <span className="vendas-popover-count-tag">
                              {tempSelectedBranches.length} / {(filtersData.filiais || []).length}
                            </span>
                          </div>
                        </div>

                        {/* Lista de Filiais */}
                        <div className="vendas-popover-list">
                          {filteredBranches.length === 0 ? (
                            <div className="vendas-popover-empty">
                              <FiHelpCircle size={24} className="d-block mx-auto mb-1 opacity-50" />
                              Nenhuma filial encontrada{branchSearch ? ` para "${branchSearch}"` : ''}.
                            </div>
                          ) : (
                            filteredBranches.map((f) => {
                              const isSelected = tempSelectedBranches.includes(String(f.codfilial));
                              return (
                                <div
                                  key={f.codfilial}
                                  className={`vendas-popover-item ${isSelected ? 'selected' : ''}`}
                                  onClick={() => handleToggleBranch(f.codfilial)}
                                >
                                  <div className="vendas-popover-item-left">
                                    <input
                                      type="checkbox"
                                      className="vendas-popover-checkbox"
                                      checked={isSelected}
                                      onChange={() => {}}
                                    />
                                    <div className="vendas-popover-avatar branch">
                                      <FiLayers size={13} />
                                    </div>
                                    <div className="vendas-popover-info">
                                      <span className="vendas-popover-name" title={f.nomefantasia || f.razaosocial}>
                                        {f.nomefantasia || f.razaosocial}
                                      </span>
                                      <div className="vendas-popover-meta">
                                        <span>Filial {f.codfilial}</span>
                                        {f.razaosocial && f.nomefantasia && (
                                          <span className="text-truncate" style={{ maxWidth: '160px' }}>• {f.razaosocial}</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="vendas-popover-item-right">
                                    <span className="vendas-popover-target-val">Cód. {f.codfilial}</span>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>

                        {/* Rodapé do Popover */}
                        <div className="vendas-popover-footer">
                          <span className="vendas-popover-footer-summary">
                            {tempSelectedBranches.length === 0
                              ? 'Todas as filiais ativas'
                              : `${tempSelectedBranches.length} filial(is) selecionada(s)`}
                          </span>
                          <div className="vendas-popover-footer-btns">
                            <button
                              type="button"
                              className="btn-modern btn-modern-light btn-sm"
                              onClick={() => setOpenBranchDropdown(false)}
                            >
                              Cancelar
                            </button>
                            <button
                              type="button"
                              className="btn-modern btn-modern-primary btn-sm"
                              onClick={handleApplyBranchSelection}
                            >
                              Aplicar
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 3. Vendedor / Colaborador - Modal Suspenso */}
              <div className="col-12 col-sm-6 col-xl-3">
                <div className="form-group-modern mb-0">
                  <span className="bi-filter-label">Vendedor / Colaborador</span>
                  <div
                    className={`vendas-search-dropdown-container ${openVendorDropdown ? 'is-open' : ''}`}
                    ref={vendorDropdownRef}
                    style={{ position: 'relative', zIndex: openVendorDropdown ? 1060 : 'auto' }}
                  >
                    <div className="vendas-search-input-wrapper">
                      <input
                        type="text"
                        readOnly
                        className="form-control-modern vendas-search-input-box"
                        value={
                          selectedVendors.length === 0 || selectedVendors.length === (filtersData.colaboradores || []).length
                            ? 'Todos os Vendedores'
                            : (selectedVendors.length === 1
                                ? `${filtersData.colaboradores.find(c => String(c.code) === String(selectedVendors[0]))?.nome || 'Colaborador'} (Cód. ${selectedVendors[0]})`
                                : `${selectedVendors.length} colaboradores selecionados`)
                        }
                        onClick={() => {
                          if (!openVendorDropdown) {
                            setTempSelectedVendors(selectedVendors);
                          }
                          setOpenVendorDropdown(!openVendorDropdown);
                        }}
                      />

                      <div className="vendas-search-actions-inside">
                        {selectedVendors.length > 0 && selectedVendors.length < (filtersData.colaboradores || []).length && (
                          <span className="vendas-selected-badge" title="Colaboradores selecionados">
                            <FiCheckSquare size={12} /> {selectedVendors.length}
                          </span>
                        )}
                        {selectedVendors.length > 0 && (
                          <FiX
                            size={14}
                            className="vendas-search-clear-icon"
                            title="Limpar seleção de colaboradores"
                            onClick={handleClearVendorSelection}
                          />
                        )}
                        <FiChevronDown
                          size={15}
                          className={`vendas-search-chevron ${openVendorDropdown ? 'open' : ''}`}
                          onClick={() => {
                            if (!openVendorDropdown) {
                              setTempSelectedVendors(selectedVendors);
                            }
                            setOpenVendorDropdown(!openVendorDropdown);
                          }}
                        />
                      </div>
                    </div>

                    {/* Popover Suspenso - Vendedor */}
                    {openVendorDropdown && (
                      <div className="vendas-search-popover popover-wide">
                        {/* Campo de Busca Interno */}
                        <div className="vendas-popover-search-bar">
                          <FiSearch size={13} className="vendas-popover-search-icon" />
                          <input
                            type="text"
                            placeholder="Buscar colaborador por nome, RCA ou código..."
                            value={vendorSearch}
                            onChange={(e) => setVendorSearch(e.target.value)}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleApplyVendorSelection();
                              } else if (e.key === 'Escape') {
                                setOpenVendorDropdown(false);
                              }
                            }}
                          />
                        </div>

                        {/* Header do Popover */}
                        <div className="vendas-popover-header">
                          <label className="vendas-popover-select-all">
                            <input
                              type="checkbox"
                              checked={allFilteredVendorsSelected}
                              ref={(el) => {
                                if (el) {
                                  el.indeterminate = someFilteredVendorsSelected && !allFilteredVendorsSelected;
                                }
                              }}
                              onChange={handleToggleSelectAllVendors}
                            />
                            <span>Selecionar todos ({filteredVendors.length})</span>
                          </label>

                          <div className="vendas-popover-header-actions">
                            {tempSelectedVendors.length > 0 && (
                              <button
                                type="button"
                                className="vendas-popover-clear-btn"
                                onClick={() => setTempSelectedVendors([])}
                              >
                                Desmarcar Todos
                              </button>
                            )}
                            <span className="vendas-popover-count-tag">
                              {tempSelectedVendors.length} / {(filtersData.colaboradores || []).length}
                            </span>
                          </div>
                        </div>

                        {/* Lista de Colaboradores */}
                        <div className="vendas-popover-list">
                          {filteredVendors.length === 0 ? (
                            <div className="vendas-popover-empty">
                              <FiHelpCircle size={24} className="d-block mx-auto mb-1 opacity-50" />
                              Nenhum colaborador encontrado{vendorSearch ? ` para "${vendorSearch}"` : ''}.
                            </div>
                          ) : (
                            filteredVendors.map((c, idx) => {
                              const isSelected = tempSelectedVendors.includes(String(c.code));
                              const initials = (c.nome || 'VD').substring(0, 2).toUpperCase();
                              const isRCA = c.type === 'vendedor_rca';
                              return (
                                <div
                                  key={`${c.code}-${idx}`}
                                  className={`vendas-popover-item ${isSelected ? 'selected' : ''}`}
                                  onClick={() => handleToggleVendor(c.code)}
                                >
                                  <div className="vendas-popover-item-left">
                                    <input
                                      type="checkbox"
                                      className="vendas-popover-checkbox"
                                      checked={isSelected}
                                      onChange={() => {}}
                                    />
                                    <div className={`vendas-popover-avatar ${isRCA ? 'rca' : 'employee'}`}>
                                      {initials}
                                    </div>
                                    <div className="vendas-popover-info">
                                      <span className="vendas-popover-name" title={c.nome}>
                                        {c.nome}
                                      </span>
                                      <div className="vendas-popover-meta">
                                        <span>🏢 Filial {c.codfilial || '-'}</span>
                                        <span>• Cód: {c.code}</span>
                                        <span className={`vendas-popover-tag ${isRCA ? 'rca' : 'employee'}`}>
                                          {isRCA ? 'Vendedor RCA' : 'Funcionário'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="vendas-popover-item-right">
                                    <span className="vendas-popover-target-val">#{c.code}</span>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>

                        {/* Rodapé do Popover */}
                        <div className="vendas-popover-footer">
                          <span className="vendas-popover-footer-summary">
                            {tempSelectedVendors.length === 0
                              ? 'Todos os colaboradores ativos'
                              : `${tempSelectedVendors.length} colaborador(es) selecionado(s)`}
                          </span>
                          <div className="vendas-popover-footer-btns">
                            <button
                              type="button"
                              className="btn-modern btn-modern-light btn-sm"
                              onClick={() => setOpenVendorDropdown(false)}
                            >
                              Cancelar
                            </button>
                            <button
                              type="button"
                              className="btn-modern btn-modern-primary btn-sm"
                              onClick={handleApplyVendorSelection}
                            >
                              Aplicar
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 4. Meta Vinculada - Modal Suspenso */}
              <div className="col-12 col-sm-6 col-xl-3">
                <div className="form-group-modern mb-0">
                  <span className="bi-filter-label">Meta Vinculada</span>
                  <div
                    className={`vendas-search-dropdown-container ${openGoalDropdown ? 'is-open' : ''}`}
                    ref={goalDropdownRef}
                    style={{ position: 'relative', zIndex: openGoalDropdown ? 1060 : 'auto' }}
                  >
                    <div className="vendas-search-input-wrapper">
                      <input
                        type="text"
                        readOnly
                        className="form-control-modern vendas-search-input-box"
                        value={
                          selectedGoals.length === 0 || selectedGoals.length === (filtersData.metas || []).length
                            ? 'Todas as Metas'
                            : (selectedGoals.length === 1
                                ? `${filtersData.metas.find(m => m.id === selectedGoals[0])?.label || `Meta #${selectedGoals[0]}`}`
                                : `${selectedGoals.length} metas selecionadas`)
                        }
                        onClick={() => {
                          if (!openGoalDropdown) {
                            setTempSelectedGoals(selectedGoals);
                          }
                          setOpenGoalDropdown(!openGoalDropdown);
                        }}
                      />

                      <div className="vendas-search-actions-inside">
                        {selectedGoals.length > 0 && selectedGoals.length < (filtersData.metas || []).length && (
                          <span className="vendas-selected-badge" title="Metas selecionadas">
                            <FiCheckSquare size={12} /> {selectedGoals.length}
                          </span>
                        )}
                        {selectedGoals.length > 0 && (
                          <FiX
                            size={14}
                            className="vendas-search-clear-icon"
                            title="Limpar seleção de metas"
                            onClick={handleClearGoalSelection}
                          />
                        )}
                        <FiChevronDown
                          size={15}
                          className={`vendas-search-chevron ${openGoalDropdown ? 'open' : ''}`}
                          onClick={() => {
                            if (!openGoalDropdown) {
                              setTempSelectedGoals(selectedGoals);
                            }
                            setOpenGoalDropdown(!openGoalDropdown);
                          }}
                        />
                      </div>
                    </div>

                    {/* Popover Suspenso - Meta */}
                    {openGoalDropdown && (
                      <div className="vendas-search-popover popover-wide popover-right">
                        {/* Campo de Busca Interno */}
                        <div className="vendas-popover-search-bar">
                          <FiSearch size={13} className="vendas-popover-search-icon" />
                          <input
                            type="text"
                            placeholder="Buscar meta por colaborador, ID ou mês..."
                            value={goalSearch}
                            onChange={(e) => setGoalSearch(e.target.value)}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleApplyGoalSelection();
                              } else if (e.key === 'Escape') {
                                setOpenGoalDropdown(false);
                              }
                            }}
                          />
                        </div>

                        {/* Header do Popover */}
                        <div className="vendas-popover-header">
                          <label className="vendas-popover-select-all">
                            <input
                              type="checkbox"
                              checked={allFilteredGoalsSelected}
                              ref={(el) => {
                                if (el) {
                                  el.indeterminate = someFilteredGoalsSelected && !allFilteredGoalsSelected;
                                }
                              }}
                              onChange={handleToggleSelectAllGoals}
                            />
                            <span>Selecionar todas ({filteredGoals.length})</span>
                          </label>

                          <div className="vendas-popover-header-actions">
                            {tempSelectedGoals.length > 0 && (
                              <button
                                type="button"
                                className="vendas-popover-clear-btn"
                                onClick={() => setTempSelectedGoals([])}
                              >
                                Desmarcar Todas
                              </button>
                            )}
                            <span className="vendas-popover-count-tag">
                              {tempSelectedGoals.length} / {(filtersData.metas || []).length}
                            </span>
                          </div>
                        </div>

                        {/* Lista de Metas */}
                        <div className="vendas-popover-list">
                          {filteredGoals.length === 0 ? (
                            <div className="vendas-popover-empty">
                              <FiHelpCircle size={24} className="d-block mx-auto mb-1 opacity-50" />
                              Nenhuma meta encontrada{goalSearch ? ` para "${goalSearch}"` : ''}.
                            </div>
                          ) : (
                            filteredGoals.map((m) => {
                              const isSelected = tempSelectedGoals.includes(m.id);
                              return (
                                <div
                                  key={m.id}
                                  className={`vendas-popover-item ${isSelected ? 'selected' : ''}`}
                                  onClick={() => handleToggleGoal(m.id)}
                                >
                                  <div className="vendas-popover-item-left">
                                    <input
                                      type="checkbox"
                                      className="vendas-popover-checkbox"
                                      checked={isSelected}
                                      onChange={() => {}}
                                    />
                                    <div className="vendas-popover-avatar goal">
                                      <FiTarget size={13} />
                                    </div>
                                    <div className="vendas-popover-info">
                                      <span className="vendas-popover-name" title={m.label}>
                                        {m.label}
                                      </span>
                                      <div className="vendas-popover-meta">
                                        <span>🏢 Filial {m.codfilial || 'Geral'}</span>
                                        <span>• Ref: {m.reference_month}/{m.reference_year}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="vendas-popover-item-right">
                                    <span className="vendas-popover-target-val">#{m.id}</span>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>

                        {/* Rodapé do Popover */}
                        <div className="vendas-popover-footer">
                          <span className="vendas-popover-footer-summary">
                            {tempSelectedGoals.length === 0
                              ? 'Todas as metas ativas'
                              : `${tempSelectedGoals.length} meta(s) selecionada(s)`}
                          </span>
                          <div className="vendas-popover-footer-btns">
                            <button
                              type="button"
                              className="btn-modern btn-modern-light btn-sm"
                              onClick={() => setOpenGoalDropdown(false)}
                            >
                              Cancelar
                            </button>
                            <button
                              type="button"
                              className="btn-modern btn-modern-primary btn-sm"
                              onClick={handleApplyGoalSelection}
                            >
                              Aplicar
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 5. Tipo de Transação */}
              <div className="col-12 col-sm-6 col-xl-2">
                <BIFilterSelect
                  label="Tipo de Transação"
                  value={filters.sale_type}
                  onChange={(val) => setFilters({ ...filters, sale_type: val })}
                  options={[
                    { value: 'venda', label: 'Vendas' },
                    { value: 'devolucao', label: 'Devoluções' }
                  ]}
                  allLabel="Todos"
                  allValue="all"
                  minWidth="100%"
                />
              </div>

              {/* 6. Período no padrão BI */}
              <div className="col-12 col-sm-12 col-xl-4">
                <BIPeriodFilter
                  startDate={filters.date_start}
                  endDate={filters.date_end}
                  onChange={(start, end) => setFilters({ ...filters, date_start: start, date_end: end })}
                  label="Início / Fim"
                  minWidth="100%"
                />
              </div>
            </div>

            {/* Barra de Ações Padronizada */}
            <div className="d-flex align-items-center gap-2 mt-3 pt-2 border-top w-100 justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <button
                  type="submit"
                  className="btn btn-primary d-flex align-items-center gap-2 px-3"
                  style={{ height: '38px', borderRadius: '8px', fontWeight: 600, fontSize: '0.84rem' }}
                  disabled={loading}
                >
                  <FiFilter size={14} />
                  <span>Aplicar Filtros</span>
                </button>
                {activeFiltersCount > 0 && (
                  <button
                    type="button"
                    className="btn btn-light border d-flex align-items-center gap-2 px-3"
                    style={{ height: '38px', borderRadius: '8px', fontWeight: 600, fontSize: '0.84rem', color: '#475569', backgroundColor: '#f8fafc' }}
                    onClick={handleResetFilters}
                    title="Limpar todos os filtros"
                  >
                    <FiX size={14} />
                    <span>Limpar ({activeFiltersCount})</span>
                  </button>
                )}
              </div>

              <button
                type="button"
                className="btn btn-light border d-flex align-items-center gap-2 px-3"
                style={{ height: '38px', borderRadius: '8px', fontWeight: 600, fontSize: '0.84rem', color: '#475569', backgroundColor: '#f8fafc' }}
                onClick={handleExport}
                disabled={exporting || items.length === 0}
                title="Exportar dados para CSV"
              >
                <FiDownload size={14} />
                <span>{exporting ? 'Exportando...' : 'Exportar'}</span>
              </button>
            </div>
          </form>
        </div>

      {/* Tabela de Vendas */}
      <div className="card-modern shadow-sm-modern" style={{ position: 'relative', zIndex: 1 }}>
        {/* Barra superior da tabela */}
        <div className="card-header-modern bg-transparent border-0 pt-4 px-4 pb-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h5 className="mb-0 fw-bold text-dark">Registros de Vendas</h5>
            <p className="text-muted small mb-0">
              Mostrando {items.length} de {summary.total_records} registros
            </p>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="small text-muted fw-medium">Linhas por página:</span>
            <select
              className="form-control-modern py-1 px-2"
              style={{ width: '85px', height: '36px', fontSize: '0.8rem' }}
              value={pagination.per_page}
              onChange={(e) => handlePerPageChange(Number(e.target.value))}
            >
              <option value="15">15</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>

        {/* Tabela */}
        <div className="table-responsive" style={{ position: 'relative' }}>
          {loading && items.length > 0 && (
            <Loading.Overlay text="Atualizando registros de vendas..." size="sm" />
          )}
          <table className="table-modern">
            <thead>
              <tr>
                <th
                  style={{ width: '105px', cursor: 'pointer' }}
                  onClick={() => handleSort('sale_date')}
                  title="Ordenar por Data"
                >
                  <div className="d-flex align-items-center gap-1">
                    <span>Data</span>
                    {sortField === 'sale_date' && (
                      <span style={{ color: 'var(--primary)', fontWeight: 'bold' }}>
                        {sortDirection === 'ASC' ? '▲' : '▼'}
                      </span>
                    )}
                  </div>
                </th>

                <th
                  style={{ width: '110px', cursor: 'pointer' }}
                  onClick={() => handleSort('order_number')}
                  title="Ordenar por Pedido"
                >
                  <div className="d-flex align-items-center gap-1">
                    <span>Pedido</span>
                    {sortField === 'order_number' && (
                      <span style={{ color: 'var(--primary)', fontWeight: 'bold' }}>
                        {sortDirection === 'ASC' ? '▲' : '▼'}
                      </span>
                    )}
                  </div>
                </th>

                <th style={{ width: '110px' }}>Tipo</th>

                <th style={{ minWidth: '130px' }}>Filial</th>

                <th style={{ minWidth: '170px' }}>Vendedor / Operador</th>

                <th style={{ minWidth: '180px' }}>Cliente</th>

                <th
                  className="text-end"
                  style={{ width: '90px', cursor: 'pointer' }}
                  onClick={() => handleSort('quantity')}
                  title="Ordenar por Quantidade"
                >
                  <div className="d-flex align-items-center justify-content-end gap-1">
                    <span>Qtd</span>
                    {sortField === 'quantity' && (
                      <span style={{ color: 'var(--primary)', fontWeight: 'bold' }}>
                        {sortDirection === 'ASC' ? '▲' : '▼'}
                      </span>
                    )}
                  </div>
                </th>

                <th
                  className="text-end"
                  style={{ width: '120px', cursor: 'pointer' }}
                  onClick={() => handleSort('gross_amount')}
                  title="Ordenar por Valor Bruto"
                >
                  <div className="d-flex align-items-center justify-content-end gap-1">
                    <span>Bruto</span>
                    {sortField === 'gross_amount' && (
                      <span style={{ color: 'var(--primary)', fontWeight: 'bold' }}>
                        {sortDirection === 'ASC' ? '▲' : '▼'}
                      </span>
                    )}
                  </div>
                </th>

                <th
                  className="text-end"
                  style={{ width: '120px', cursor: 'pointer' }}
                  onClick={() => handleSort('discount_amount')}
                  title="Ordenar por Devolução"
                >
                  <div className="d-flex align-items-center justify-content-end gap-1">
                    <span>Devolução</span>
                    {sortField === 'discount_amount' && (
                      <span style={{ color: 'var(--primary)', fontWeight: 'bold' }}>
                        {sortDirection === 'ASC' ? '▲' : '▼'}
                      </span>
                    )}
                  </div>
                </th>

                <th
                  className="text-end"
                  style={{ width: '130px', cursor: 'pointer' }}
                  onClick={() => handleSort('net_amount')}
                  title="Ordenar por Valor Líquido"
                >
                  <div className="d-flex align-items-center justify-content-end gap-1">
                    <span>Líquido</span>
                    {sortField === 'net_amount' && (
                      <span style={{ color: 'var(--primary)', fontWeight: 'bold' }}>
                        {sortDirection === 'ASC' ? '▲' : '▼'}
                      </span>
                    )}
                  </div>
                </th>

                <th style={{ minWidth: '150px' }}>Meta / Filtro</th>

                <th className="text-center" style={{ width: '70px' }}>Ações</th>
              </tr>
            </thead>

            <tbody>
              {loading && items.length === 0 ? (
                <Loading.Table
                  colSpan={12}
                  text="Carregando registros de vendas..."
                  subtext="Buscando cupons, itens faturados e devoluções do ERP..."
                />
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan="12" className="text-center py-5">
                    <div
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '16px',
                        background: '#f1f5f9',
                        color: 'var(--text-muted)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '12px'
                      }}
                    >
                      <FiSearch size={26} />
                    </div>
                    <h6 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>
                      Nenhuma venda encontrada
                    </h6>
                    <p className="text-muted small mb-3">
                      Tente ajustar ou limpar os filtros para visualizar mais resultados.
                    </p>
                    {activeFiltersCount > 0 && (
                      <button
                        type="button"
                        className="btn-modern btn-modern-light btn-modern-sm"
                        onClick={handleResetFilters}
                      >
                        <FiX size={14} className="me-1" />
                        Limpar Filtros
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                items.map((sale) => {
                  const isDevolucao = sale.sale_type === 'devolucao';
                  return (
                    <tr
                      key={sale.id}
                      style={{
                        backgroundColor: isDevolucao ? '#fff5f5' : undefined
                      }}
                    >
                      {/* Data */}
                      <td className="text-nowrap fw-medium" style={{ color: 'var(--text-main)' }}>
                        {formatDate(sale.sale_date)}
                      </td>

                      {/* Pedido */}
                      <td className="text-nowrap">
                        <span
                          className="badge-modern badge-modern-secondary"
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            letterSpacing: '0.02em',
                            ...(isDevolucao ? { background: '#fee2e2', color: '#991b1b' } : {})
                          }}
                        >
                          #{sale.order_number}
                        </span>
                      </td>

                      {/* Tipo */}
                      <td className="text-nowrap">
                        {isDevolucao ? (
                          <span className="badge-modern badge-modern-danger">
                            <FiArrowDownRight size={13} />
                            <span>Devolução</span>
                          </span>
                        ) : (
                          <span className="badge-modern badge-modern-success">
                            <FiArrowUpRight size={13} />
                            <span>Venda</span>
                          </span>
                        )}
                      </td>

                      {/* Filial */}
                      <td className="text-nowrap">
                        <span className="fw-semibold text-dark">
                          Filial {sale.branch_code}
                        </span>
                        {sale.branch_name && sale.branch_name !== `Filial ${sale.branch_code}` && (
                          <span className="d-block text-muted" style={{ fontSize: '0.74rem' }}>
                            {sale.branch_name}
                          </span>
                        )}
                      </td>

                      {/* Vendedor */}
                      <td style={{ maxWidth: '200px' }} title={sale.vendor_name}>
                        <div className="d-flex align-items-center gap-1">
                          <FiUser size={13} className="text-muted flex-shrink-0" />
                          <span className="fw-medium text-dark text-truncate">{sale.vendor_name}</span>
                        </div>
                        <span className="text-muted d-block" style={{ fontSize: '0.72rem' }}>
                          Cód. {sale.vendor_code}
                        </span>
                      </td>

                      {/* Cliente */}
                      <td style={{ maxWidth: '210px' }} title={sale.customer_name}>
                        <div className="fw-medium text-dark text-truncate">{sale.customer_name || '-'}</div>
                        {sale.codcli && (
                          <span className="text-muted d-block" style={{ fontSize: '0.72rem' }}>
                            Cód. {sale.codcli}
                          </span>
                        )}
                      </td>

                      {/* Quantidade */}
                      <td className="text-end text-nowrap fw-semibold text-secondary">
                        {formatNumber(sale.quantity, 0)}
                      </td>

                      {/* Valor Bruto */}
                      <td className="text-end text-nowrap text-muted">
                        {formatCurrency(sale.gross_amount)}
                      </td>

                      {/* Devoluções / Desconto */}
                      <td className="text-end text-nowrap">
                        {sale.discount_amount > 0 ? (
                          <span style={{ color: 'var(--danger)', fontWeight: 600 }}>
                            -{formatCurrency(sale.discount_amount)}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>

                      {/* Valor Líquido */}
                      <td className="text-end text-nowrap">
                        <span
                          style={{
                            fontWeight: 700,
                            color: sale.net_amount < 0 ? 'var(--danger)' : 'var(--success)',
                            fontSize: '0.92rem'
                          }}
                        >
                          {formatCurrency(sale.net_amount)}
                        </span>
                      </td>

                      {/* Meta / Filtro */}
                      <td className="text-nowrap" style={{ maxWidth: '180px' }}>
                        {sale.goal_id ? (
                          <div className="d-flex flex-column gap-1">
                            <span
                              className="badge-modern badge-modern-info text-truncate d-inline-block"
                              style={{ maxWidth: '160px', fontSize: '0.7rem' }}
                              title={`Meta #${sale.goal_id}`}
                            >
                              Meta #{sale.goal_id}
                            </span>
                            {sale.filter_name && (
                              <span
                                className="text-muted small text-truncate d-block"
                                style={{ fontSize: '0.72rem' }}
                                title={sale.filter_name}
                              >
                                {sale.filter_name}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted small">-</span>
                        )}
                      </td>

                      {/* Ações */}
                      <td className="text-center text-nowrap">
                        <button
                          type="button"
                          className="btn-modern btn-modern-light btn-modern-sm"
                          style={{ padding: '6px 8px' }}
                          onClick={() => setSelectedSale(sale)}
                          title="Ver detalhes da venda"
                        >
                          <FiEye size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Rodapé e Paginação */}
        <BIPagination
          page={pagination.current_page}
          perPage={pagination.per_page}
          total={summary.total_records || pagination.total}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
          itemLabel="vendas"
          perPageOptions={[15, 25, 50, 100, 200]}
        />
      </div>

      {/* Modal de Detalhes da Venda */}
      {selectedSale && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(4px)',
            padding: '20px'
          }}
          onClick={(e) => e.target === e.currentTarget && setSelectedSale(null)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '20px',
              width: '100%',
              maxWidth: '750px',
              maxHeight: '90vh',
              boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
              animation: 'modalSlideIn 0.25s ease',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* Header do Modal */}
            <div
              style={{
                padding: '24px 28px 20px',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: isDevolucaoSelected ? '#fee2e2' : '#eff6ff',
                    color: isDevolucaoSelected ? 'var(--danger)' : 'var(--primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FiShoppingBag size={22} />
                </div>
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <h5 style={{ margin: 0, fontWeight: 700, fontSize: '1.05rem', color: 'var(--text-main)' }}>
                      Pedido #{selectedSale.order_number}
                    </h5>
                    <span className={`badge-modern ${isDevolucaoSelected ? 'badge-modern-danger' : 'badge-modern-success'}`}>
                      {isDevolucaoSelected ? 'Devolução' : 'Venda'}
                    </span>
                  </div>
                  <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    Transação realizada em {formatDate(selectedSale.sale_date)}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setSelectedSale(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '8px',
                  color: 'var(--text-muted)'
                }}
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Corpo do Modal */}
            <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
              {/* Destaque Financeiro */}
              <div className="stats-grid-modern mb-4" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                <div className="stat-card-modern p-3">
                  <div className="stat-content">
                    <div className="stat-label-modern" style={{ fontSize: '0.75rem' }}>Valor Bruto</div>
                    <div className="stat-value-modern" style={{ fontSize: '1.15rem' }}>
                      {formatCurrency(selectedSale.gross_amount)}
                    </div>
                  </div>
                </div>

                <div className="stat-card-modern p-3">
                  <div className="stat-content">
                    <div className="stat-label-modern" style={{ fontSize: '0.75rem' }}>Devoluções</div>
                    <div className="stat-value-modern" style={{ fontSize: '1.15rem', color: 'var(--danger)' }}>
                      {formatCurrency(selectedSale.discount_amount)}
                    </div>
                  </div>
                </div>

                <div className="stat-card-modern p-3">
                  <div className="stat-content">
                    <div className="stat-label-modern" style={{ fontSize: '0.75rem' }}>Valor Líquido</div>
                    <div
                      className="stat-value-modern"
                      style={{
                        fontSize: '1.15rem',
                        color: selectedSale.net_amount < 0 ? 'var(--danger)' : 'var(--success)'
                      }}
                    >
                      {formatCurrency(selectedSale.net_amount)}
                    </div>
                  </div>
                </div>

                <div className="stat-card-modern p-3">
                  <div className="stat-content">
                    <div className="stat-label-modern" style={{ fontSize: '0.75rem' }}>Quantidade</div>
                    <div className="stat-value-modern" style={{ fontSize: '1.15rem' }}>
                      {formatNumber(selectedSale.quantity, 0)} <span style={{ fontSize: '0.75rem' }}>un</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Informações da Transação */}
              <div style={{ marginBottom: '24px' }}>
                <div
                  style={{
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'var(--text-muted)',
                    marginBottom: '12px'
                  }}
                >
                  Dados da Transação
                </div>

                <div className="row g-3">
                  <div className="col-sm-6">
                    <div
                      style={{
                        padding: '14px 16px',
                        borderRadius: '12px',
                        border: '1px solid var(--border)',
                        background: '#f8fafc',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}
                    >
                      <FiCalendar size={20} color="var(--text-muted)" />
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Data da Transação</div>
                        <div style={{ fontWeight: 600, color: 'var(--text-main)', fontSize: '0.88rem' }}>
                          {formatDate(selectedSale.sale_date)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div
                      style={{
                        padding: '14px 16px',
                        borderRadius: '12px',
                        border: '1px solid var(--border)',
                        background: '#f8fafc',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}
                    >
                      <FiBox size={20} color="var(--text-muted)" />
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Filial</div>
                        <div style={{ fontWeight: 600, color: 'var(--text-main)', fontSize: '0.88rem' }}>
                          Filial {selectedSale.branch_code} {selectedSale.branch_name ? `- ${selectedSale.branch_name}` : ''}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div
                      style={{
                        padding: '14px 16px',
                        borderRadius: '12px',
                        border: '1px solid var(--border)',
                        background: '#f8fafc',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}
                    >
                      <FiUser size={20} color="var(--text-muted)" />
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Cliente</div>
                        <div style={{ fontWeight: 600, color: 'var(--text-main)', fontSize: '0.88rem' }}>
                          {selectedSale.customer_name || 'Consumidor'}
                        </div>
                        {selectedSale.codcli && (
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                            Código: {selectedSale.codcli}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div
                      style={{
                        padding: '14px 16px',
                        borderRadius: '12px',
                        border: '1px solid var(--border)',
                        background: '#f8fafc',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}
                    >
                      <FiUser size={20} color="var(--text-muted)" />
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Vendedor / Operador</div>
                        <div style={{ fontWeight: 600, color: 'var(--text-main)', fontSize: '0.88rem' }}>
                          {selectedSale.vendor_name}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                          Código: {selectedSale.vendor_code} ({selectedSale.vendor_type === 'funcionario' ? 'Funcionário' : 'Vendedor RCA'})
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Vínculo de Meta e Filtro */}
              {(selectedSale.goal_id || selectedSale.filter_name) && (
                <div style={{ marginBottom: '24px' }}>
                  <div
                    style={{
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: 'var(--text-muted)',
                      marginBottom: '12px'
                    }}
                  >
                    Vínculo com Metas & Apuração
                  </div>

                  <div
                    style={{
                      padding: '16px',
                      borderRadius: '12px',
                      border: '1px solid var(--border)',
                      background: '#f8fafc'
                    }}
                  >
                    <div className="row g-3">
                      {selectedSale.goal_id && (
                        <div className="col-sm-6">
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                            Meta Associada:
                          </span>
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge-modern badge-modern-info">
                              <FiTarget size={13} />
                              Meta #{selectedSale.goal_id}
                            </span>
                            {selectedSale.goal_classification && (
                              <span className="badge-modern badge-modern-secondary" style={{ fontSize: '0.7rem' }}>
                                {selectedSale.goal_classification}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {selectedSale.filter_name && (
                        <div className="col-sm-6">
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                            Filtro de Apuração:
                          </span>
                          <div className="d-flex align-items-center gap-1">
                            <span className="badge-modern badge-modern-secondary">
                              <FiLayers size={13} />
                              {selectedSale.filter_name}
                            </span>
                            {selectedSale.filter_id && (
                              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                (ID #{selectedSale.filter_id})
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Rodapé do Modal */}
            <div
              style={{
                padding: '16px 28px 24px',
                borderTop: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                flexShrink: 0
              }}
            >
              <button
                type="button"
                className="btn-modern btn-modern-light"
                onClick={() => setSelectedSale(null)}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Estilos complementares */}
      <style>{`
        .spin-ico {
          animation: spin-ico 0.8s linear infinite;
          display: inline-block;
        }
        @keyframes spin-ico {
          to { transform: rotate(360deg); }
        }
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
